import * as React from 'react'

import { Content } from 'sdk/components'
import { Text, TextName } from 'sdk'
import { Button } from 'sdk/button/Button'
import { ContactUsSection } from 'sdk/sections'
import { NewsfeedBigCard } from 'pages/newsfeed/components/newsfeed-big-card/NewsfeedBigCard'
import { ShowcaseFeatureDisplay } from 'pages/soundscape-page/sections/showcase/ShowcaseFeatureDisplay'
import { showcaseFeatureCards } from 'pages/soundscape-page/sections/showcase/showcaseFeatureCards'
import CyberBuckShowcase from 'pages/soundscape-page/sections/cyber-buck/CyberBuckShowcase'
import showcaseBackgroundDesktop from 'pages/soundscape-page/assets/soundscape-showcase-background-desktop.jpg'
import showcaseBackgroundMobile from 'pages/soundscape-page/assets/soundscape-showcase-background-mobile.jpg'
import { NewsfeedEntry, NewsfeedRecordType } from 'core/interfaces'

import cesHeroImage from './assets/ces-hero.jpg'
import demoVideo from './assets/cybertruck-demo.webm'
import moreToComeImage from './assets/more-to-come.png'

import styles from './Ces2026Page.module.scss'

const heroEntry: NewsfeedEntry = {
  id: 0,
  title: 'Join Us During CES 2026',
  preview: 'At CES 2026, experience the world\'s first in-vehicle demonstration of true multi-zone car audio with active zone compensation in a real vehicle. This breakthrough is enabled by real-time, GPU-accelerated audio processing running directly on the car\'s infotainment platform, using as little as 2\u20133% of its available compute capacity.',
  type: NewsfeedRecordType.Event,
  views: 0,
  pathname: 'ces2026',
  bg_image: {
    url: cesHeroImage,
    collage: { url: cesHeroImage },
  },
  event_type: 'CES 2026',
  registration_link: 'mailto:info@gpu.audio?subject=Multi-zone%20Car%20Audio%20Player%20Live%20Demo%20Reservation',
  human_date: '5\u20139 Jan',
  human_time: '2026',
}

export const Ces2026Page: React.FC = () => {
  return (
    <Content
      gap={150}
      first
      className={styles.content}
    >
      <NewsfeedBigCard
        entry={heroEntry}
        showLearnMore={false}
        descriptionExtra={(
          <Text type={TextName.Text}>
            <strong>Location:</strong> CES 2026, Venetian Expo, Eureka Park, Hall G, booth 60846
          </Text>
        )}
        actions={(
          <>
            <Button
              as="link"
              href="mailto:info@gpu.audio?subject=Multi-zone%20Car%20Audio%20Player%20Live%20Demo%20Reservation"
              target="_blank"
            >
              Book a demo
            </Button>
            <Button
              as="link"
              href="https://drive.google.com/drive/folders/1OPddWb9t30SJMnK2aOXkJPPHNv6U66rO?usp=sharing"
              target="_blank"
            >
              Media kit
            </Button>
          </>
        )}
      />

      <section className={styles.section}>
        <Text type={TextName.H2}>About GPU Audio</Text>
        <Text type={TextName.Text}>
          GPU Audio has solved the fundamental challenge of running advanced audio DSP and Neural Network algorithms on GPUs, unlocking massive parallel processing power that can transform audio in new ways. Accelerated Audio Computing will change how people record, edit, perform, perceive, and experience audio. With patented technology years in the making, the GPU Audio solutions suite enables audio system developers to tap into the underutilized GPU power grid to enhance current signal processing and build totally new sonic experiences. From automotive in-cabin sound to workflows for Pro Audio creators, technology from GPU Audio is the engine for audio innovation.
        </Text>
      </section>

      <section className={styles.sectionGap75}>
        <Text type={TextName.H2}>About Multi-Zone Player</Text>
        <ShowcaseFeatureDisplay
          desktopBackgroundUrl={showcaseBackgroundDesktop}
          mobileBackgroundUrl={showcaseBackgroundMobile}
          cards={showcaseFeatureCards}
        />
        <Button
          as="router-link"
          to="/soundscape"
          className={styles.centeredButton}
        >
          Explore Soundscape
        </Button>
      </section>

      <CyberBuckShowcase />

      <section className={styles.section}>
        <Text type={TextName.H2}>Soundscape Demo App</Text>
        <Text type={TextName.Text}>
          The demo app demonstrates seamless integration of multi-zone playback into the car infotainment GUI. It enables flexible, one-click assignment of diverse sound sources to individual seats or combinations, tailored to real-world scenarios, like the driver on a conference call while kids watch cartoons, or navigation prompts for the driver alongside a movie for second-row passengers.
        </Text>
      </section>

      <section className={styles.sectionGap75}>
        <Text type={TextName.H2}>Check Out Our Demo Video</Text>
        <video className={styles.demoVideo} controls>
          <source src={demoVideo} type="video/webm" />
        </video>
      </section>

      <section className={styles.sectionGap75}>
        <Text type={TextName.H2}>More to Come</Text>
        <img
          className={styles.moreImage}
          src={moreToComeImage}
          alt="More to come"
        />
        <Text type={TextName.Text}>
          Soundscape technology extends beyond today's commercial multi-zone players, enabling a rapid rollout of innovative GPU-powered audio solutions, like real-time noise suppression, multi-source sound mixing, and more. Looking ahead, it empowers car OEMs to migrate all audio processing to the infotainment GPU, eliminating traditional DSPs entirely.
        </Text>
      </section>

      <div className={styles.contactSection}>
        <ContactUsSection />
      </div>
      <div className={styles.afterContactGap} />
    </Content>
  )
}
